import { isTransportFailure } from "../../GraphQL/utils/networkFailureTracker";

/**
 * Web RUM errors that are only a browser's dropped-connection wording
 * (DHT-5802). After DHT-5616 the GraphQL error link already reports these as
 * `network_request_failed` actions, but they still reach RUM as errors when
 * something re-raises them or never catches them:
 *
 * - an unhandled rejection of an Apollo operation whose only cause is the
 *   transport TypeError (the link has already counted it);
 * - an uncaught fetch TypeError from a library (react-native-skia's
 *   `Skia.Data.fromURI`) or with no stack at all (Safari);
 * - a console line that is exactly the wording (Segment analytics logs its
 *   failed settings fetch with `console.error(e.message)`).
 *
 * The wording itself comes from `isTransportFailure`, so the GraphQL links and
 * this filter can never disagree on what a transport failure is.
 *
 * Our own `logger.error` / `addError` calls (`custom` source) are never
 * matched: those call sites decide for themselves (DHT-5748).
 */

export type TransportFailure = {
  errorSource: string;
  errorType: string | null;
  handling: string | null;
  message: string;
};

type RumErrorShape = {
  source?: unknown;
  type?: unknown;
  message?: unknown;
  handling?: unknown;
  causes?: unknown;
  resource?: { status_code?: unknown } | null;
};

const isTransportTypeError = (type: unknown, message: unknown) =>
  typeof type === "string" &&
  typeof message === "string" &&
  isTransportFailure({ name: type, message }, undefined);

// Every cause must be a transport TypeError: a ServerError (it has a status),
// a GraphQL error, or a mix keeps the event an error.
const allCausesAreTransport = (causes: unknown) =>
  Array.isArray(causes) &&
  causes.length > 0 &&
  causes.every(
    cause =>
      !!cause &&
      typeof cause === "object" &&
      isTransportTypeError(
        (cause as { type?: unknown }).type,
        (cause as { message?: unknown }).message,
      ),
  );

const matches = (error: RumErrorShape): boolean => {
  if (error.resource?.status_code != null) return false;

  if (error.source === "source") {
    if (error.type === "TypeError") {
      return isTransportTypeError(error.type, error.message);
    }
    if (error.type === "ApolloError") {
      return (
        isTransportTypeError("TypeError", error.message) &&
        allCausesAreTransport(error.causes)
      );
    }
    return false;
  }

  // A console line carrying only a fetch TypeError's message.
  if (error.source === "console") {
    return (
      error.type == null && isTransportTypeError("TypeError", error.message)
    );
  }

  return false;
};

/**
 * The transport failure an error event carries, or null to keep it as an
 * error. Never throws: anything it cannot read is kept, because losing a real
 * error is worse than keeping noise, and a throw inside `beforeSend` would
 * break every event.
 */
export const transportFailureOf = (event: unknown): TransportFailure | null => {
  try {
    const rumEvent = event as { type?: unknown; error?: unknown } | null;
    if (rumEvent?.type !== "error") return null;
    const error = rumEvent.error;
    if (!error || typeof error !== "object") return null;
    const shape = error as RumErrorShape;
    if (!matches(shape)) return null;

    return {
      errorSource: shape.source as string,
      errorType: typeof shape.type === "string" ? shape.type : null,
      handling: typeof shape.handling === "string" ? shape.handling : null,
      // Only ever one of the browsers' fixed wordings, so no user text.
      message: shape.message as string,
    };
  } catch {
    return null;
  }
};
